import { useEffect, useState } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { emitToast } from '../../context/toast';

const DEFAULT_WELLNESS = {
  sleep_hours: 7,
  sleep_quality: 7,
  stress_level: 4,
  appetite: 7,
  energy_level: 7,
  soreness: 3,
};

export default function TrainingWellnessPanel() {
  const { t } = useI18n();
  const [plan, setPlan] = useState(null);
  const [wellness, setWellness] = useState(DEFAULT_WELLNESS);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    api.get('/training/my-current-plan')
      .then((r) => setPlan(r.data?.data || r.data?.plan || null))
      .catch(() => setPlan(null));
  }, []);

  const updateField = (key, value) => {
    setWellness((prev) => ({ ...prev, [key]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/training/log', {
        plan_id: plan?.id || null,
        dia: plan?.dia_actual || 1,
        ejercicios: [],
        completado: false,
        duration_minutes: 0,
        wellness,
      });
      setLastSaved(new Date());
      emitToast({
        type: 'success',
        title: t('wellness.saved', 'Encuesta guardada'),
        message: t('wellness.saved_hint', 'Tu registro de bienestar quedó registrado para hoy.'),
      });
    } catch (err) {
      emitToast({
        type: 'error',
        title: t('wellness.error', 'Error'),
        message: err.response?.data?.error || t('wellness.save_fail', 'No se pudo guardar la encuesta'),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5">
      <p className="text-sm d28d-text-muted">
        {t('wellness.intro', 'Cuéntanos cómo te sientes hoy: sueño, estrés, energía y apetito. Tu coach usa estos datos para ajustar tu plan.')}
      </p>
      <div className="card p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <FieldNumber
          label={t('wellness.sleep_hours', 'Sueño (horas)')}
          value={wellness.sleep_hours}
          onChange={(v) => updateField('sleep_hours', v)}
          min={0}
          max={14}
          step={0.5}
        />
        <FieldRange
          label={t('wellness.sleep_quality', 'Calidad de sueño')}
          value={wellness.sleep_quality}
          onChange={(v) => updateField('sleep_quality', v)}
        />
        <FieldRange
          label={t('wellness.stress', 'Estrés')}
          value={wellness.stress_level}
          onChange={(v) => updateField('stress_level', v)}
        />
        <FieldRange
          label={t('wellness.appetite', 'Apetito')}
          value={wellness.appetite}
          onChange={(v) => updateField('appetite', v)}
        />
        <FieldRange
          label={t('wellness.energy', 'Energía')}
          value={wellness.energy_level}
          onChange={(v) => updateField('energy_level', v)}
        />
        <FieldRange
          label={t('wellness.soreness', 'Molestias / DOMS')}
          value={wellness.soreness}
          onChange={(v) => updateField('soreness', v)}
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? '…' : t('wellness.save', 'Guardar encuesta de hoy')}
        </button>
        {lastSaved ? (
          <span className="text-xs d28d-text-muted">
            {t('wellness.last_saved', 'Último registro')}: {lastSaved.toLocaleTimeString()}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function FieldNumber({ label, value, onChange, min, max, step }) {
  return (
    <label className="text-sm block space-y-1">
      <span className="font-semibold text-[var(--d28d-text)]">{label}</span>
      <input
        type="number"
        className="input w-full"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function FieldRange({ label, value, onChange }) {
  return (
    <label className="text-sm block space-y-1">
      <span className="font-semibold text-[var(--d28d-text)]">{label} ({value}/10)</span>
      <input
        type="range"
        min={1}
        max={10}
        className="w-full accent-[var(--d28d-yellow)]"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}
